import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useController,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { ChevronDown, X } from "lucide-react";
import FormFieldShell from "./FormFieldShell";
import { cn } from "@/lib/utils";

export interface OptionItem {
  label: string;
  value: string | number;
}

export interface SelectDropdownProps<TFieldValues extends FieldValues = any> {
  label?: string;
  placeholder?: string;
  options: OptionItem[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  onSearch?: (searchQuery: string) => void;
  className?: string;
  dropDownContainerClass?: string;
  control?: Control<TFieldValues>;
  name?: Path<TFieldValues>;
  required?: boolean;
  disabled?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  isSearchable?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  onMenuScrollToBottom?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

interface SelectDropdownBaseProps {
  options: OptionItem[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  onSearch?: (searchQuery: string) => void;
  placeholder?: string;
  className?: string;
  dropDownContainerClass?: string;
  shouldShowError?: boolean;
  disabled?: boolean;
  isSearchable?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  id?: string;
  onMenuScrollToBottom?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

const SelectDropdownBase: React.FC<SelectDropdownBaseProps> = ({
  options,
  value,
  onChange,
  onSearch,
  placeholder = "Select...",
  className,
  dropDownContainerClass,
  shouldShowError = false,
  disabled = false,
  isSearchable = false,
  searchable = false,
  clearable = false,
  id,
  onMenuScrollToBottom,
  hasMore,
  isLoadingMore,
}) => {
  const enableSearch = Boolean(isSearchable || searchable);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const optionsMapRef = useRef<Map<string, OptionItem>>(new Map());

  // Maintain historical options map so selected item label is retained when searching remotely
  useEffect(() => {
    options.forEach((opt) => {
      if (opt?.value !== undefined && opt?.value !== null) {
        optionsMapRef.current.set(String(opt.value), opt);
      }
    });
  }, [options]);

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchText(text);
      setActiveIndex(0);
      onSearch?.(text);
    },
    [onSearch],
  );

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 20) {
      if (hasMore && !isLoadingMore && onMenuScrollToBottom) {
        onMenuScrollToBottom();
      }
    }
  };

  const filteredOptions =
    enableSearch && !onSearch
      ? options.filter((o) =>
          o.label.toLowerCase().includes(searchText.toLowerCase()),
        )
      : options;

  const selectedOption =
    options.find((o) => o.value === value) ||
    (value !== undefined && value !== null && value !== ""
      ? optionsMapRef.current.get(String(value))
      : undefined);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        if (searchText !== "") {
          handleSearchChange("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchText, handleSearchChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
        const idx = options.findIndex((o) => o.value === value);
        setActiveIndex(idx >= 0 ? idx : 0);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const nextIndex = Math.min(
          prev >= 0 ? prev + 1 : 0,
          filteredOptions.length - 1,
        );
        optionRefs.current[nextIndex]?.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
        return nextIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const nextIndex = Math.max(prev - 1, 0);
        optionRefs.current[nextIndex]?.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
        return nextIndex;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const option = filteredOptions[activeIndex >= 0 ? activeIndex : 0];
      if (option) {
        handleSelect(option.value);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      if (searchText !== "") {
        handleSearchChange("");
      }
    }
  };

  const handleSelect = (optionValue: string | number) => {
    onChange?.(optionValue);
    setIsOpen(false);
    if (searchText !== "") {
      handleSearchChange("");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.("");
    if (searchText !== "") {
      handleSearchChange("");
    }
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    const nextOpen = !isOpen;
    if (!isOpen) {
      const idx = options.findIndex((o) => o.value === value);
      setActiveIndex(idx >= 0 ? idx : 0);
    } else {
      if (searchText !== "") {
        handleSearchChange("");
      }
    }
    setIsOpen(nextOpen);
  };

  return (
    <div
      ref={wrapperRef}
      id={id}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      className={cn("relative w-full text-left outline-none", className)}
    >
      {/* Trigger Control Box */}
      <div
        onClick={handleTriggerClick}
        className={cn(
          "w-full h-10 px-3.5 flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 text-sm transition-all cursor-pointer select-none",
          shouldShowError &&
            "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500",
          isOpen &&
            !shouldShowError &&
            "ring-2 ring-purple-500 border-purple-500 dark:ring-purple-400",
          disabled &&
            "opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-900",
        )}
      >
        <div className="flex-1 min-w-0 flex items-center">
          {isOpen && enableSearch ? (
            <input
              ref={searchInputRef}
              type="text"
              autoFocus
              placeholder={selectedOption ? selectedOption.label : placeholder}
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              className="w-full bg-transparent border-none outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 font-medium text-sm p-0 m-0 focus:outline-none focus:ring-0"
            />
          ) : (
            <span
              className={cn(
                "truncate font-medium",
                !selectedOption?.value && "text-slate-400 dark:text-slate-500",
              )}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-2">
          {clearable && selectedOption && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 rounded-full"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown
            className={cn(
              "w-4 h-4 text-slate-400 transition-transform duration-200",
              isOpen && "rotate-180 text-purple-600 dark:text-purple-400",
            )}
          />
        </div>
      </div>

      {/* Dropdown Options Container */}
      {isOpen && (
        <div
          className={cn(
            "absolute left-0 right-0 top-[calc(100%+4px)] z-50 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-xl overflow-hidden animate-in fade-in-50 zoom-in-95 duration-100",
            dropDownContainerClass,
          )}
        >
          <ul
            onScroll={handleScroll}
            className="max-h-56 overflow-y-auto p-1 text-sm space-y-0.5"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isActive = index === activeIndex;

                return (
                  <li
                    key={option.value}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(option.value);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      "px-3 py-2 rounded-lg cursor-pointer transition-colors flex items-center justify-between text-slate-700 dark:text-slate-300 font-medium",
                      (isSelected || isActive) &&
                        "bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 font-semibold",
                      !isSelected &&
                        !isActive &&
                        "hover:bg-slate-100 dark:hover:bg-slate-900",
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && (
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-600 dark:bg-purple-400 shrink-0 ml-2" />
                    )}
                  </li>
                );
              })
            ) : (
              <li className="px-3 py-4 text-center text-xs text-slate-400 dark:text-slate-500">
                No options found
              </li>
            )}
            {isLoadingMore && (
              <li className="px-3 py-2 text-center text-xs text-purple-600 dark:text-purple-400 font-medium flex items-center justify-center gap-1.5">
                <span className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                Loading more...
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const SelectDropdownControlled = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  required,
  labelClassName,
  errorClassName,
  wrapperClassName = "w-full",
  ...rest
}: SelectDropdownProps<TFieldValues>) => {
  const { field, fieldState, formState } = useController({
    control: control!,
    name: name!,
  });

  const shouldShowError =
    !!(fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;

  return (
    <FormFieldShell
      error={shouldShowError ? fieldState.error?.message : undefined}
      htmlFor={name}
      label={label}
      labelClassName={labelClassName}
      errorClassName={errorClassName}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <SelectDropdownBase
        {...rest}
        id={name}
        value={field.value ?? ""}
        onChange={(val) => field.onChange(val)}
        shouldShowError={shouldShowError}
      />
    </FormFieldShell>
  );
};

const SelectDropdownUncontrolled = ({
  label,
  required,
  labelClassName,
  errorClassName,
  wrapperClassName = "w-full",
  value,
  onChange,
  ...rest
}: SelectDropdownProps) => {
  return (
    <FormFieldShell
      htmlFor={rest.placeholder}
      label={label}
      labelClassName={labelClassName}
      errorClassName={errorClassName}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <SelectDropdownBase {...rest} value={value} onChange={onChange} />
    </FormFieldShell>
  );
};

export const SelectDropdown = <TFieldValues extends FieldValues = any>(
  props: SelectDropdownProps<TFieldValues>,
) => {
  if (props.control && props.name) {
    return <SelectDropdownControlled {...props} />;
  }
  return <SelectDropdownUncontrolled {...props} />;
};

export default SelectDropdown;
